import { GAME_ASSET_SLUG } from "@/config/game";
import { Dropdown, DropdownItem } from "@/design-system/Dropdown/Dropdown";
import { useTranslation } from "@repo/shared/i18n";
import { ReactNode, useMemo } from "react";
import { useSelector } from "react-redux";

import { flagIcon, leagueIcon } from "../../../../../designSystemIcons";
import { loadedContentSelector } from "../../../../../redux/loadedContent/loadedContentSlice";
import { IconDropDown } from "../../../../atoms/Dropdown/IconDropDown";
import { toLeagueItems } from "../../RightPanel/FilterDropdown/FilterDropdown";

const validManagerCountries = new Set([
  1, 4, 5, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24,
  25, 27, 29, 34, 35, 36, 37, 38, 39, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51,
  52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 70, 72, 80, 82, 83, 87, 92, 95, 97,
  101, 103, 104, 108, 110, 111, 115, 116, 117, 118, 119, 125, 126, 129, 133,
  136, 140, 145, 155, 159, 163, 167, 183, 195, 198, 219,
]);

const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;

type Props = {
  onSelect: (id: number, type: "country" | "league") => void;
  country: number;
  league: number;
  disabled?: boolean;
};
export const SelectManager = ({
  country,
  league,
  onSelect,
  disabled,
}: Props) => {
  const { t } = useTranslation(["squadBuilder", "common"]);
  const { countries: loadedCountries, leagues: loadedLeagues } = useSelector(
    loadedContentSelector,
  );

  const countryItems = useMemo<DropdownItem[]>(
    () =>
      loadedCountries
        .filter((c) => validManagerCountries.has(c.id))
        .map((c) => ({
          value: String(c.id),
          label: c.name,
          icon: (
            <img
              className="h-4"
              src={`${assetsBaseUrl}/fc25/countries/${c.id}.png`}
              alt={c.name}
            />
          ),
        })),
    [loadedCountries],
  );

  const leagueItems = useMemo(
    () => toLeagueItems(loadedLeagues),
    [loadedLeagues],
  );

  // Picking the selected row again clears it (the reducer toggles)
  const picker = (
    type: "country" | "league",
    items: DropdownItem[],
    selected: number,
    icon: ReactNode,
  ) =>
    disabled ? (
      <IconDropDown icon={icon} isOpen={false} disabled />
    ) : (
      <Dropdown
        items={items}
        selected={selected ? String(selected) : undefined}
        onChange={(item) => onSelect(Number(item.value), type)}
        placeholder={t("pitch.manager")}
        trigger={(open) => <IconDropDown icon={icon} isOpen={open} />}
      />
    );

  const countryPicker = picker(
    "country",
    countryItems,
    country,
    country ? (
      <img
        className="w-6 my-auto"
        src={`${assetsBaseUrl}/fc25/countries/${country}.png`}
        alt={t("common:alt.country")}
      />
    ) : (
      <div className="w-6 h-6 my-auto">{flagIcon}</div>
    ),
  );
  const leaguePicker = picker(
    "league",
    leagueItems,
    league,
    league ? (
      <img
        className="w-6 my-auto"
        src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/leagues/dark/${league}.png`}
        alt={t("common:alt.league")}
      />
    ) : (
      <div className="w-6 h-6 my-auto">{leagueIcon}</div>
    ),
  );

  return (
    <div className="flex flex-col items-center px-2 pt-1 pb-2 rounded-lg touch-device:border touch-device:h-16 touch-device:bg-white/10 touch-device:border-white/10 gap-y-1">
      <span className="hidden mr-auto text-sm font-medium text-center text-white touch-device:flex">
        {t("pitch.manager")}
      </span>
      <div className="flex gap-x-2 text-secondary-dark h-7">
        {countryPicker}
        {leaguePicker}
      </div>
    </div>
  );
};
